import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Zap, Target, Trash2, RefreshCw, ShieldCheck, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LuckyDuckyQAAdminTab
 * =====================
 * Admin-only QA tooling.
 *
 * Preview / QA screen-recording aid: arms a one-shot
 * FORCE BETO RESPINS — NEXT SPIN flag on a specified test player.
 * The next spin that player initiates lands a deterministic ≥3-BONUS
 * base grid, entering the same feature machine an organic trigger uses.
 * The forced spin bypasses the wallet entirely (no debit, no credit,
 * no wagering totals) and is tagged in the audit so RTP / analytics can
 * filter it out.
 *
 * This UI is ONLY reachable inside the admin dashboard, behind the
 * existing admin authentication.  There is no client-side toggle, URL
 * parameter, or localStorage path that can activate the same flag.
 */
export default function LuckyDuckyQAAdminTab() {
    const [email, setEmail] = useState('blakeplayer@yahoo.com');
    // Comma-separated list of mock +N free-play awards to inject on the
    // sequenced feature spins.  Empty = no mocks.  Purely PRESENTATION
    // testing — never changes wallet, math, or feature length.
    const [mockAwards, setMockAwards] = useState('0,0,0,5,0,0,15,0,0,0,25');
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busyAction, setBusyAction] = useState(null);

    const authHeaders = useMemo(() => {
        const token = localStorage.getItem('token');
        return token ? { Authorization: `Bearer ${token}` } : {};
    }, []);

    const refreshStatus = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/lucky-ducky/qa/status`, {
                headers: authHeaders,
            });
            if (!res.ok) throw new Error(`Status ${res.status}`);
            const data = await res.json();
            setStatus(data);
        } catch (err) {
            toast.error(`Could not load QA status: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [authHeaders]);

    useEffect(() => { refreshStatus(); }, [refreshStatus]);

    const arm = async () => {
        if (!email.trim()) {
            toast.error('Enter a target player email first.');
            return;
        }
        setBusyAction('arm');
        try {
            const parsedMocks = mockAwards
                .split(',').map(v => parseInt(v.trim(), 10))
                .filter(v => Number.isFinite(v) && v >= 0);
            const res = await fetch(`${API}/lucky-ducky/qa/arm-force-respins`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders },
                body: JSON.stringify({
                    target_user_email: email.trim().toLowerCase(),
                    mock_retrigger_awards: parsedMocks,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || `Status ${res.status}`);
            toast.success(`ARMED — next spin from ${data.target_email} will trigger BETO RESPINS`);
            await refreshStatus();
        } catch (err) {
            toast.error(`Arm failed: ${err.message}`);
        } finally {
            setBusyAction(null);
        }
    };

    const disarm = async () => {
        setBusyAction('disarm');
        try {
            const res = await fetch(`${API}/lucky-ducky/qa/disarm-force-respins`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders },
                body: JSON.stringify({ target_user_email: email.trim().toLowerCase() }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || `Status ${res.status}`);
            toast.success(`Disarmed ${data.disarmed} pending flag(s) for ${data.target_email}`);
            await refreshStatus();
        } catch (err) {
            toast.error(`Disarm failed: ${err.message}`);
        } finally {
            setBusyAction(null);
        }
    };

    const armed = status?.armed || [];
    const recent = status?.recently_consumed || [];

    return (
        <div className="space-y-6 text-white" data-testid="lucky-ducky-qa-tab">
            {/* Hero card */}
            <div className="rounded-2xl border border-[#FFD700]/40 bg-gradient-to-br from-[#1a1400] to-[#0a0800] p-6 shadow-lg shadow-yellow-500/10">
                <div className="flex items-start gap-4">
                    <div className="rounded-xl bg-[#FFD700]/20 p-3">
                        <Zap className="h-7 w-7 text-[#FFD700]" />
                    </div>
                    <div className="flex-1">
                        <h2 className="text-xl font-bold text-[#FFD700]">FORCE BETO RESPINS — NEXT SPIN</h2>
                        <p className="mt-1 text-sm text-neutral-300">
                            Preview / QA only.  Arms a one-shot flag on the specified test player.
                            Their next spin lands a legitimate ≥3-BONUS trigger and enters the same
                            feature state machine an organic trigger uses.
                        </p>
                        <ul className="mt-3 space-y-1 text-xs text-neutral-400">
                            <li>· Wallet is <span className="text-emerald-400 font-semibold">NOT touched</span> — no debit, no credit, no wagering totals.</li>
                            <li>· Spin animation is <span className="text-emerald-400 font-semibold">identical</span> to a natural trigger (same WAAPI reel motion, same feature presentation).</li>
                            <li>· Flag is <span className="text-emerald-400 font-semibold">one-shot</span> — clears automatically after the spin.  The following spin returns to normal RNG.</li>
                            <li>· Every forced spin is tagged <code className="rounded bg-black/40 px-1 text-yellow-200">test_mode=true, forced_result=true, test_reason=BETO_RESPINS_QA</code> — excluded from RTP / financial reporting.</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Arm form */}
            <div className="rounded-2xl border border-neutral-700 bg-neutral-900 p-6">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-200">
                    <Target className="h-4 w-4 text-[#FFD700]" /> Target test player
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="player@example.com"
                        data-testid="qa-target-email-input"
                        className="min-w-[280px] flex-1 rounded-lg border border-neutral-600 bg-black/50 px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:border-[#FFD700] focus:outline-none"
                    />
                </div>
                <div className="mt-4">
                    <label className="mb-2 block text-xs font-semibold text-neutral-400">
                        Mock <span className="text-[#FFD700]">+N FREE PLAYS</span> per feature spin (comma-separated).
                        PRESENTATION ONLY — does not extend feature length or change any math.
                    </label>
                    <input
                        type="text"
                        value={mockAwards}
                        onChange={(e) => setMockAwards(e.target.value)}
                        placeholder="0,0,5,0,15,0,25"
                        data-testid="qa-mock-awards-input"
                        className="w-full rounded-lg border border-neutral-700 bg-black/50 px-3 py-2 font-mono text-sm text-white placeholder:text-neutral-500 focus:border-[#FFD700] focus:outline-none"
                    />
                    <p className="mt-1 text-[11px] text-neutral-500">
                        Example: <span className="font-mono">0,0,0,5,0,0,15,0,0,0,25</span> fires +5 on spin 4, +15 on spin 7, +25 on spin 11.
                    </p>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button
                        onClick={arm}
                        disabled={busyAction === 'arm'}
                        data-testid="qa-arm-force-respins-btn"
                        className="inline-flex items-center gap-2 rounded-lg bg-[#FFD700] px-4 py-2 text-sm font-bold text-black transition hover:brightness-110 disabled:opacity-50"
                    >
                        <Zap className="h-4 w-4" />
                        {busyAction === 'arm' ? 'Arming…' : 'ARM FORCE BETO RESPINS — NEXT SPIN'}
                    </button>
                    <button
                        onClick={disarm}
                        disabled={busyAction === 'disarm'}
                        data-testid="qa-disarm-btn"
                        className="inline-flex items-center gap-2 rounded-lg border border-red-500/60 bg-red-950/40 px-3 py-2 text-sm font-semibold text-red-200 transition hover:bg-red-900/40 disabled:opacity-50"
                    >
                        <Trash2 className="h-4 w-4" />
                        Disarm
                    </button>
                    <button
                        onClick={refreshStatus}
                        disabled={loading}
                        data-testid="qa-refresh-btn"
                        className="inline-flex items-center gap-2 rounded-lg border border-neutral-600 px-3 py-2 text-xs text-neutral-300 hover:bg-neutral-800"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Currently armed */}
            <div className="rounded-2xl border border-neutral-700 bg-neutral-900 p-6" data-testid="qa-armed-list">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-emerald-300">
                    <ShieldCheck className="h-4 w-4" /> Currently armed ({armed.length})
                </h3>
                {armed.length === 0 ? (
                    <p className="text-xs text-neutral-500">No players currently armed.  Arm a test player above, then have them tap SPIN in Preview.</p>
                ) : (
                    <ul className="space-y-2">
                        {armed.map((row) => (
                            <li key={row._id} className="rounded-lg border border-emerald-700/40 bg-emerald-950/20 p-3 text-xs">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                        <div className="font-semibold text-emerald-200">{row.target_email}</div>
                                        <div className="text-neutral-400">user_id: <span className="font-mono text-neutral-300">{row.user_id}</span></div>
                                    </div>
                                    <div className="text-right text-neutral-500">
                                        armed by <span className="text-neutral-300">{row.armed_by_admin_email || row.armed_by_admin_id}</span>
                                        <br />
                                        {row.armed_at ? new Date(row.armed_at).toLocaleString() : ''}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Recently consumed */}
            <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-6" data-testid="qa-recent-list">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-400">
                    <AlertCircle className="h-4 w-4" /> Recently consumed ({recent.length})
                </h3>
                {recent.length === 0 ? (
                    <p className="text-xs text-neutral-600">No forced spins consumed yet.</p>
                ) : (
                    <ul className="space-y-1.5">
                        {recent.map((row) => (
                            <li key={row._id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-neutral-800 bg-neutral-900/60 px-3 py-2 text-xs">
                                <div className="text-neutral-300">{row.target_email || row.user_id}</div>
                                <div className="text-neutral-500">
                                    spin: <span className="font-mono text-neutral-400">{(row.consumed_spin_id || '').slice(0, 8)}</span>
                                    {row.consumed_at ? ` · ${new Date(row.consumed_at).toLocaleTimeString()}` : ''}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Usage */}
            <div className="rounded-2xl border border-neutral-800 bg-neutral-950/70 p-5 text-xs text-neutral-400">
                <div className="mb-2 font-semibold text-neutral-200">How to use for screen recording</div>
                <ol className="list-inside list-decimal space-y-1">
                    <li>Enter the test player&apos;s email above (default is the QA player).</li>
                    <li>Click <span className="font-semibold text-[#FFD700]">ARM FORCE BETO RESPINS — NEXT SPIN</span>.</li>
                    <li>Open Preview in another tab/window and log in as that test player.</li>
                    <li>Navigate to Lucky Ducky and start your screen recording.</li>
                    <li>Tap SPIN once.  The reels animate normally, land ≥3 BONUS symbols, and drop straight into the feature.</li>
                    <li>The flag consumes itself — the second spin returns to normal RNG.</li>
                </ol>
            </div>
        </div>
    );
}
